import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { ChevronLeft, ChevronRight, ShoppingBag, Package, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Product Card with translation
const TranslatedProductCard = ({ product, language, translateContent, viewDetails }) => {
  const [translatedDesc, setTranslatedDesc] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    const translateDescription = async () => {
      const roDesc = product.descriptions?.ro;
      const enDesc = product.descriptions?.en || product.description || '';
      
      // ROMANIAN
      if (language === 'ro') {
        if (roDesc) {
          setTranslatedDesc(roDesc);
        } else if (enDesc) {
          setIsTranslating(true);
          try {
            const translated = await translateContent(enDesc, 'product', `${product.id}_carousel_ro`);
            setTranslatedDesc(translated);
          } catch (e) {
            setTranslatedDesc(enDesc);
          } finally {
            setIsTranslating(false);
          }
        }
        return;
      }
      
      // ENGLISH
      if (language === 'en') {
        setTranslatedDesc(enDesc || roDesc || '');
        return;
      }
      
      // OTHER (German, etc.) - translate with AI
      const sourceText = roDesc || enDesc;
      if (sourceText) {
        setIsTranslating(true);
        try {
          const translated = await translateContent(sourceText, 'product', `${product.id}_carousel_${language}`);
          setTranslatedDesc(translated);
        } catch (e) {
          setTranslatedDesc(sourceText);
        } finally {
          setIsTranslating(false);
        }
      }
    };

    translateDescription();
  }, [product, language, translateContent]);

  return (
    <Link
      to={`/products/${product.id}`}
      className="group w-56 flex-shrink-0 bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
    >
      {/* Image */}
      <div className="aspect-square bg-slate-100 p-3">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      
      {/* Content */}
      <div className="p-4">
        <p className="text-xs text-sky-600 font-medium mb-1">{product.category}</p>
        <h3 className="font-semibold text-slate-900 text-sm mb-2 line-clamp-1 group-hover:text-sky-600 transition-colors">
          {product.name}
        </h3>
        {isTranslating ? (
          <div className="flex items-center gap-1 mb-3">
            <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
            <span className="text-xs text-slate-400">...</span>
          </div>
        ) : (
          <p className="text-xs text-slate-500 line-clamp-2 mb-3">
            {translatedDesc.substring(0, 60)}...
          </p>
        )}
        <span className="text-sky-600 text-sm font-medium flex items-center gap-1">
          {viewDetails}
          <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </Link>
  );
};

const ProductsCarousel = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const { language, translateContent } = useLanguage();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/products`);
        setProducts(response.data || []);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Translations
  const translations = {
    en: {
      sectionTitle: "Our Products",
      subtitle: "Discover LifeWave phototherapy patches",
      viewDetails: "View",
      viewAll: "View All Products"
    },
    ro: {
      sectionTitle: "Produsele Noastre",
      subtitle: "Descoperă plasturii de fototerapie LifeWave",
      viewDetails: "Vezi",
      viewAll: "Vezi Toate Produsele"
    },
    de: {
      sectionTitle: "Unsere Produkte",
      subtitle: "Entdecke LifeWave Phototherapie-Pflaster",
      viewDetails: "Ansehen",
      viewAll: "Alle Produkte Ansehen"
    }
  };

  const text = translations[language] || translations['en'];

  if (loading) {
    return (
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-4 overflow-hidden">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="w-64 flex-shrink-0 bg-white rounded-2xl p-4 animate-pulse">
                <div className="aspect-square bg-slate-200 rounded-xl mb-3"></div>
                <div className="h-4 bg-slate-200 rounded w-2/3 mb-2"></div>
                <div className="h-3 bg-slate-200 rounded w-full"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) return null;

  return (
    <section className="py-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-700 px-3 py-1 rounded-full text-sm font-medium mb-2">
              <Package className="w-4 h-4" />
              LifeWave
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
              {text.sectionTitle}
            </h2>
            <p className="text-slate-600 mt-1">{text.subtitle}</p>
          </div>
          
          {/* Navigation Arrows */}
          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="p-2 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow text-slate-600 hover:text-sky-600"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => scroll('right')}
              className="p-2 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow text-slate-600 hover:text-sky-600"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Products Carousel */}
        <div 
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {products.map((product) => (
            <TranslatedProductCard
              key={product.id}
              product={product}
              language={language}
              translateContent={translateContent}
              viewDetails={text.viewDetails}
            />
          ))}
        </div>

        {/* View All Link */}
        <div className="text-center mt-8">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-6 py-3 rounded-full font-medium transition-colors"
          >
            <ShoppingBag className="w-4 h-4" />
            {text.viewAll}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProductsCarousel;
